import { useState } from 'react';
import { XIcon, BrainIcon, Loader2Icon } from 'lucide-react';
import type { SnapCategory } from '@/types/database.types';

interface AiDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onGetOpinion: (modes: SnapCategory[]) => Promise<void>;
  aiOpinion: string | null;
  loadingAi: boolean;
}

export default function AiDialog({
  isOpen,
  onClose,
  onGetOpinion,
  aiOpinion,
  loadingAi
}: AiDialogProps) {
  const [selectedModes, setSelectedModes] = useState<SnapCategory[]>([]);

  const toggleMode = (mode: SnapCategory) => {
    setSelectedModes(prev =>
      prev.includes(mode)
        ? prev.filter(m => m !== mode)
        : [...prev, mode]
    );
  };

  const handleClose = () => {
    onClose();
    setSelectedModes([]);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Get AI Opinion</h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {!aiOpinion ? (
          <>
            <p className="text-gray-600 mb-4">
              Select the types of feedback you'd like to receive from the AI:
            </p>
            <div className="space-y-2 mb-6">
              {(['emotional', 'rational', 'ethical', 'economic'] as const).map((mode) => (
                <label key={mode} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectedModes.includes(mode)}
                    onChange={() => toggleMode(mode)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-gray-700 capitalize">{mode}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => onGetOpinion(selectedModes)}
                disabled={loadingAi || selectedModes.length === 0}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {loadingAi ? (
                  <>
                    <Loader2Icon className="animate-spin -ml-1 mr-2 h-4 w-4" />
                    Getting Opinion...
                  </>
                ) : (
                  <>
                    <BrainIcon className="h-4 w-4 mr-2" />
                    Get AI Opinion
                  </>
                )}
              </button>
            </div>
          </>
        ) : (
          <div className="prose max-w-none">
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="whitespace-pre-wrap text-gray-700">{aiOpinion}</p>
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleClose}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}